import { ArrowLeftRight, Plus, TrendingUp } from "lucide-react";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import { useVisibleScenarios } from "@/hooks/useVisibleScenarios";
import { useTerminology } from "@/hooks/useTerminology";
import type { ComprehensiveScenario } from "@/types/scenarios";

interface ScenarioComparisonCardProps {
	onNavigate?: (page: string, data?: Record<string, unknown>) => void;
}

type DsIntent = "success" | "warning" | "info" | "destructive";

// Status → DS semantic intent
const STATUS_INTENT: Record<string, DsIntent> = {
	active: "success",
	committed: "success",
	approved: "success",
	"pending-approval": "warning",
	"under-review": "info",
	draft: "info",
	parked: "info",
	rejected: "destructive",
	archived: "info",
};

// Intent → card left-border + subtle background using DS accent CSS variables
const INTENT_CARD_CLASS: Record<DsIntent, string> = {
	success:
		"border-l-4 border-l-[var(--accent-success-500)]     bg-[var(--accent-success-50)]",
	warning:
		"border-l-4 border-l-[var(--accent-warning-500)]     bg-[var(--accent-warning-50)]",
	destructive:
		"border-l-4 border-l-[var(--accent-destructive-500)] bg-[var(--accent-destructive-50)]",
	info: "border-l-4 border-l-[var(--accent-info-500)]        bg-[var(--accent-info-50)]",
};

// Category → colour dot using DS accent colours
const CATEGORY_COLOURS: Record<string, string> = {
	growth: "bg-[var(--accent-success-500)]",
	"cost-cutting": "bg-[var(--accent-info-500)]",
	crisis: "bg-[var(--accent-destructive-500)]",
};

export default function ScenarioComparisonCard({
	onNavigate,
}: ScenarioComparisonCardProps) {
	const { hasPermission } = usePermissions();
	const { formatCurrency } = useFormatting();
	const visibleScenarios = useVisibleScenarios();
	const { getScenarioTerm, getCashDurationShortTerm, getCashDurationTerm } = useTerminology();

	if (!hasPermission("view_scenario_comparison_card")) {
		return null;
	}

	const canCreate = hasPermission("create_scenarios");
	const canCompare = hasPermission("compare_scenarios");

	// Show the 3 most relevant scenarios — active first, then by modified date
	const displayScenarios: ComprehensiveScenario[] = visibleScenarios
		.filter((s) => s.status !== "archived" && s.status !== "rejected")
		.sort((a, b) => {
			const statusPriority: Record<string, number> = {
				active: 0,
				approved: 1,
				"pending-approval": 3,
				"under-review": 4,
				draft: 5,
				parked: 6,
			};
			const pa = statusPriority[a.status] ?? 9;
			const pb = statusPriority[b.status] ?? 9;
			if (pa !== pb) return pa - pb;
			return b.modifiedAt.getTime() - a.modifiedAt.getTime();
		})
		.slice(0, 3);

	const totalVisible = visibleScenarios.filter(
		(s) => s.status !== "archived" && s.status !== "rejected",
	).length;

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<CardTitle className="flex items-center gap-2">
						<TrendingUp className="h-5 w-5" />
						{getScenarioTerm()} Comparison
					</CardTitle>
					<InfoTooltip
						title={`${getScenarioTerm()} Comparison`}
						content="Your top scenarios ranked by status and activity. Active and approved scenarios appear first. Click Compare Scenarios to view a full side-by-side analysis."
					/>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				{displayScenarios.length === 0 ? (
					<div className="py-6 text-center text-sm text-muted-foreground">
						No scenarios yet. Create your first scenario to get started.
					</div>
				) : (
					displayScenarios.map((scenario) => (
						<div
							key={scenario.id}
							className={`p-3 rounded-lg cursor-pointer transition-colors hover:brightness-95 ${INTENT_CARD_CLASS[STATUS_INTENT[scenario.status] ?? "info"]}`}
							onClick={() => onNavigate?.("scenarios")}
						>
							<div className="flex items-start justify-between mb-2 gap-2">
								<div className="min-w-0">
									<div className="flex items-center gap-2 mb-0.5">
										{scenario.category &&
											CATEGORY_COLOURS[scenario.category] && (
												<span
													className={`h-2 w-2 rounded-full flex-shrink-0 ${CATEGORY_COLOURS[scenario.category]}`}
												/>
											)}
										<div className="font-semibold text-sm truncate">
											{scenario.name}
										</div>
									</div>
									<div className="text-xs text-muted-foreground truncate">
										{scenario.description}
									</div>
								</div>
								<Badge
									variant={STATUS_INTENT[scenario.status] ?? "info"}
									className="flex-shrink-0 text-xs capitalize"
								>
									{scenario.status.replace(/-/g, " ")}
								</Badge>
							</div>
							<div className="grid grid-cols-3 gap-2 text-xs">
								<div>
									<div className="text-muted-foreground">Min Cash</div>
									<div className="font-semibold">
										{formatCurrency(scenario.metrics.minCash)}
									</div>
								</div>
								<div>
									<div className="text-muted-foreground capitalize">
										{getCashDurationShortTerm()}
									</div>
									<div className="font-semibold">
										{getCashDurationTerm(scenario.metrics.runway)}
									</div>
								</div>
								<div>
									<div className="text-muted-foreground">Net Impact</div>
									<div
										className={`font-semibold ${scenario.metrics.netImpact >= 0 ? "text-[var(--accent-success-500)]" : "text-[var(--accent-destructive-500)]"}`}
									>
										{scenario.metrics.netImpact >= 0 ? "+" : ""}
										{formatCurrency(scenario.metrics.netImpact)}
									</div>
								</div>
							</div>
						</div>
					))
				)}

				{totalVisible > 3 && (
					<button
						type="button"
						onClick={() => onNavigate?.("scenarios")}
						className="text-xs text-primary hover:underline text-center w-full"
					>
						+{totalVisible - 3} more scenario{totalVisible - 3 !== 1 ? "s" : ""}{" "}
						— view all in Scenarios
					</button>
				)}

				{(canCreate || canCompare) && (
					<div
						className={`grid ${canCreate && canCompare ? "grid-cols-2" : "grid-cols-1"} gap-2 pt-1`}
					>
						{canCreate && (
							<Button
								variant="outline"
								className="w-full"
								onClick={() =>
									onNavigate?.("scenarios", { openCreateModal: true })
								}
							>
								<Plus className="h-4 w-4 mr-1" />
								New Scenario
							</Button>
						)}
						{canCompare && (
							<Button
								variant="outline"
								className="w-full"
								onClick={() =>
									onNavigate?.("scenarios", { scenarioTab: "comparison" })
								}
							>
								<ArrowLeftRight className="h-4 w-4 mr-1" />
								Compare
							</Button>
						)}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
